import * as React from 'react'
import { Plus, X, Wand2, Settings2, HelpCircle, Zap } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'

// ============================================================================
// Constraint Taxonomy - matches orchestrator/src/Plugins/ConstraintHelper.cs
// ============================================================================

export interface ConstraintType {
  category: string
  subtype: string
  description: string
  targetAgents: string[]
  placeholder: string
  examples: string[]
}

/**
 * Constraint taxonomy from the orchestrator.
 * Format: category:subtype:value
 */
export const CONSTRAINT_TYPES: ConstraintType[] = [
  {
    category: 'scope',
    subtype: 'path',
    description: 'Restrict which files/directories can be modified',
    targetAgents: ['Planner', 'Coder', 'Reviewer'],
    placeholder: 'Only modify src/api/**',
    examples: [
      'Only modify files in Pages/, Models/, Services/',
      'Only modify src/**',
      'Do not modify test files',
    ],
  },
  {
    category: 'scope',
    subtype: 'size',
    description: 'Limit the size of changes',
    targetAgents: ['Coder'],
    placeholder: 'Max 100 lines changed',
    examples: [
      'Max 100 lines changed',
      'Keep changes under 50 lines per file',
      'Maximum 5 files modified',
    ],
  },
  {
    category: 'scope',
    subtype: 'readonly',
    description: 'Prevent modification of specified files or directories',
    targetAgents: ['Coder'],
    placeholder: 'open-api-defs/**',
    examples: [
      'open-api-defs/**',
      'docs/**',
      '*.generated.ts',
    ],
  },
  {
    category: 'scope',
    subtype: 'exception',
    description: 'Grant exceptions for convention-based paths (e.g., test projects)',
    targetAgents: ['Coder', 'Reviewer'],
    placeholder: '/Project.Tests/** (unit test project)',
    examples: [
      '/HelloWorldApp.Tests/** (unit test project)',
      '/MyProject.IntegrationTests/** (integration tests)',
    ],
  },
  {
    category: 'quality',
    subtype: 'tests',
    description: 'Testing requirements for the changes',
    targetAgents: ['Coder', 'Tester'],
    placeholder: 'Include unit tests for new methods',
    examples: [
      'Include unit tests for TaskService methods',
      'Must include unit tests',
      'Add integration tests for API endpoints',
    ],
  },
  {
    category: 'quality',
    subtype: 'coverage',
    description: 'Minimum test coverage percentage required',
    targetAgents: ['Coder', 'Tester'],
    placeholder: '80% code coverage required',
    examples: [
      '80% code coverage required',
      'Maintain at least 70% line coverage',
      '90% coverage for new code',
    ],
  },
  {
    category: 'quality',
    subtype: 'documentation',
    description: 'Documentation requirements for code changes',
    targetAgents: ['Coder', 'Reviewer'],
    placeholder: 'Add JSDoc comments to public APIs',
    examples: [
      'Add JSDoc comments to public APIs',
      'Update README for new features',
      'Include inline comments for complex logic',
    ],
  },
  {
    category: 'security',
    subtype: 'secrets',
    description: 'Prevent hardcoded secrets and sensitive data',
    targetAgents: ['Coder', 'Reviewer'],
    placeholder: 'No hardcoded API keys or passwords',
    examples: [
      'No hardcoded API keys or passwords',
      'Use environment variables for secrets',
      'No sensitive data in logs',
    ],
  },
  {
    category: 'security',
    subtype: 'packages',
    description: 'Restrict or approve package dependencies',
    targetAgents: ['Coder'],
    placeholder: 'Only use approved packages from internal registry',
    examples: [
      'Only use approved packages from internal registry',
      'No packages with known vulnerabilities',
      'Prefer well-maintained packages (>1000 stars)',
    ],
  },
  {
    category: 'tech',
    subtype: 'framework',
    description: 'Framework and library requirements',
    targetAgents: ['Planner', 'Coder', 'Reviewer'],
    placeholder: 'Use React 18 with hooks',
    examples: [
      'Use React 18 with hooks',
      'Use .NET 8 minimal APIs',
      'Use TanStack Query for data fetching',
    ],
  },
  {
    category: 'tech',
    subtype: 'language',
    description: 'Programming language requirements',
    targetAgents: ['Planner', 'Coder', 'Reviewer'],
    placeholder: 'Use TypeScript with strict mode',
    examples: [
      'Use TypeScript with strict mode',
      'Use C# 12 features',
      'Python 3.11+ only',
    ],
  },
  {
    category: 'tech',
    subtype: 'pattern',
    description: 'Design patterns and architectural requirements',
    targetAgents: ['Coder', 'Reviewer'],
    placeholder: 'Use repository pattern for data access',
    examples: [
      'Use repository pattern for data access',
      'Follow CQRS pattern',
      'Use dependency injection',
    ],
  },
  {
    category: 'review',
    subtype: 'style',
    description: 'Code style and documentation requirements',
    targetAgents: ['Reviewer'],
    placeholder: 'Enforce XML doc comments',
    examples: [
      'Enforce XML doc comments on public methods',
      'Follow clean code principles',
      'Use async/await pattern consistently',
    ],
  },
  {
    category: 'search',
    subtype: 'repos',
    description: 'Limit which repositories to search',
    targetAgents: ['Planner'],
    placeholder: 'Only search org/backend-*',
    examples: [
      'Only search org/backend-*',
      'Search myorg/api-* repositories',
    ],
  },
]

/**
 * Common constraint templates for quick addition
 */
export const CONSTRAINT_TEMPLATES = [
  {
    label: 'Limit to src folder',
    value: 'scope:path:Only modify files in src/**',
    category: 'scope',
  },
  {
    label: 'Include unit tests',
    value: 'quality:tests:Include unit tests for new methods',
    category: 'quality',
  },
  {
    label: '80% test coverage',
    value: 'quality:coverage:80% code coverage required',
    category: 'quality',
  },
  {
    label: 'Max 100 lines',
    value: 'scope:size:Max 100 lines changed',
    category: 'scope',
  },
  {
    label: 'XML documentation',
    value: 'quality:documentation:Add XML doc comments on public members',
    category: 'quality',
  },
  {
    label: 'No hardcoded secrets',
    value: 'security:secrets:No hardcoded API keys or passwords',
    category: 'security',
  },
  {
    label: 'Use TypeScript strict',
    value: 'tech:language:Use TypeScript with strict mode',
    category: 'tech',
  },
  {
    label: 'Use dependency injection',
    value: 'tech:pattern:Use dependency injection for services',
    category: 'tech',
  },
  {
    label: 'No test file changes',
    value: 'scope:path:Do not modify test files',
    category: 'scope',
  },
  {
    label: 'Readonly OpenAPI defs',
    value: 'scope:readonly:open-api-defs/**',
    category: 'scope',
  },
  {
    label: 'Follow existing patterns',
    value: 'review:style:Follow existing code patterns and conventions',
    category: 'review',
  },
  {
    label: 'Maintain backward compatibility',
    value: 'review:style:Maintain backward compatibility with existing APIs',
    category: 'review',
  },
]

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Parse a constraint string into its parts
 */
export function parseConstraint(constraint: string): {
  category: string
  subtype: string
  value: string
  isStructured: boolean
} {
  const parts = constraint.split(':')
  if (parts.length >= 3) {
    const [category, subtype, ...rest] = parts
    // Check if it matches a known prefix
    const isKnown = CONSTRAINT_TYPES.some(
      (t) => t.category === category && t.subtype === subtype
    )
    if (isKnown) {
      return {
        category,
        subtype,
        value: rest.join(':'),
        isStructured: true,
      }
    }
  }
  return {
    category: '',
    subtype: '',
    value: constraint,
    isStructured: false,
  }
}

/**
 * Build a constraint string from parts
 */
export function buildConstraint(
  category: string,
  subtype: string,
  value: string
): string {
  if (!category || !subtype || !value.trim()) return ''
  return `${category}:${subtype}:${value.trim()}`
}

/**
 * Get the constraint type definition for a category:subtype
 */
export function getConstraintType(
  category: string,
  subtype: string
): ConstraintType | undefined {
  return CONSTRAINT_TYPES.find(
    (t) => t.category === category && t.subtype === subtype
  )
}

/**
 * Get unique categories from constraint types
 */
export function getCategories(): string[] {
  return [...new Set(CONSTRAINT_TYPES.map((t) => t.category))]
}

/**
 * Get subtypes for a category
 */
export function getSubtypes(category: string): string[] {
  return CONSTRAINT_TYPES.filter((t) => t.category === category).map(
    (t) => t.subtype
  )
}

// ============================================================================
// Subcomponents
// ============================================================================

interface ConstraintBadgeProps {
  constraint: string
  className?: string
}

/**
 * Badge showing constraint category with color coding
 */
function ConstraintBadge({ constraint, className }: ConstraintBadgeProps) {
  const parsed = parseConstraint(constraint)
  if (!parsed.isStructured) return null

  const colorMap: Record<string, string> = {
    scope: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
    quality: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
    review: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
    search: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  }

  return (
    <Badge
      variant="secondary"
      className={cn(
        'text-[10px] px-1.5 py-0 font-medium',
        colorMap[parsed.category] || '',
        className
      )}
    >
      {parsed.category}:{parsed.subtype}
    </Badge>
  )
}

interface ConstraintHelpProps {
  category: string
  subtype: string
}

/**
 * Help popover showing constraint details
 */
function ConstraintHelp({ category, subtype }: ConstraintHelpProps) {
  const type = getConstraintType(category, subtype)
  if (!type) return null

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-muted-foreground hover:text-foreground"
        >
          <HelpCircle className="h-3.5 w-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="start">
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="font-mono text-xs">
              {category}:{subtype}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">{type.description}</p>
          <div className="text-xs">
            <span className="font-medium">Target agents: </span>
            <span className="text-muted-foreground">
              {type.targetAgents.join(', ')}
            </span>
          </div>
          <div className="pt-2 border-t">
            <p className="text-xs font-medium mb-1">Examples:</p>
            <ul className="text-xs text-muted-foreground space-y-1">
              {type.examples.slice(0, 3).map((ex, i) => (
                <li key={i} className="pl-2 border-l-2 border-muted">
                  {ex}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

// ============================================================================
// Builder Mode Component
// ============================================================================

interface ConstraintBuilderProps {
  onAdd: (constraint: string) => void
  disabled?: boolean
}

function ConstraintBuilder({ onAdd, disabled }: ConstraintBuilderProps) {
  const [category, setCategory] = React.useState<string>('')
  const [subtype, setSubtype] = React.useState<string>('')
  const [value, setValue] = React.useState('')

  const selectedType = category && subtype ? getConstraintType(category, subtype) : null

  const handleAdd = () => {
    const constraint = buildConstraint(category, subtype, value)
    if (constraint) {
      onAdd(constraint)
      setCategory('')
      setSubtype('')
      setValue('')
    }
  }

  const handleCategoryChange = (newCategory: string) => {
    setCategory(newCategory)
    setSubtype('')
    setValue('')
  }

  const handleSubtypeChange = (newSubtype: string) => {
    setSubtype(newSubtype)
    setValue('')
  }

  const isValid = category && subtype && value.trim()

  return (
    <div className="space-y-3 p-3 border rounded-lg bg-muted/30">
      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        <Settings2 className="h-4 w-4" />
        Constraint Builder
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Select value={category} onValueChange={handleCategoryChange} disabled={disabled}>
          <SelectTrigger className="h-8 text-xs">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectLabel>Categories</SelectLabel>
              {getCategories().map((cat) => (
                <SelectItem key={cat} value={cat} className="text-xs">
                  {cat}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>

        <Select
          value={subtype}
          onValueChange={handleSubtypeChange}
          disabled={disabled || !category}
        >
          <SelectTrigger className="h-8 text-xs">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectLabel>Types</SelectLabel>
              {category &&
                getSubtypes(category).map((st) => (
                  <SelectItem key={st} value={st} className="text-xs">
                    {st}
                  </SelectItem>
                ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center gap-2">
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={selectedType?.placeholder || 'Enter constraint value...'}
          disabled={disabled || !category || !subtype}
          className="flex-1 h-8 text-sm"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && isValid) {
              e.preventDefault()
              handleAdd()
            }
          }}
        />
        {selectedType && <ConstraintHelp category={category} subtype={subtype} />}
        <Button
          type="button"
          size="sm"
          onClick={handleAdd}
          disabled={disabled || !isValid}
          className="h-8"
        >
          <Plus className="h-3.5 w-3.5 mr-1" />
          Add
        </Button>
      </div>

      {selectedType && (
        <p className="text-xs text-muted-foreground">
          {selectedType.description}
        </p>
      )}
    </div>
  )
}

// ============================================================================
// Template Mode Component
// ============================================================================

interface ConstraintTemplatesProps {
  onAdd: (constraint: string) => void
  existingConstraints: string[]
  disabled?: boolean
}

function ConstraintTemplates({
  onAdd,
  existingConstraints,
  disabled,
}: ConstraintTemplatesProps) {
  // Filter out already added templates
  const availableTemplates = CONSTRAINT_TEMPLATES.filter(
    (t) => !existingConstraints.includes(t.value)
  )

  if (availableTemplates.length === 0) {
    return (
      <p className="text-xs text-muted-foreground text-center py-2">
        All templates have been added
      </p>
    )
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        <Zap className="h-4 w-4" />
        Quick Add Templates
      </div>
      <div className="flex flex-wrap gap-1.5">
        {availableTemplates.map((template) => (
          <Button
            key={template.value}
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onAdd(template.value)}
            disabled={disabled}
            className="h-7 text-xs"
          >
            <Plus className="h-3 w-3 mr-1" />
            {template.label}
          </Button>
        ))}
      </div>
    </div>
  )
}

// ============================================================================
// Main Component
// ============================================================================

export type ConstraintInputMode = 'simple' | 'builder'

export interface ConstraintsInputProps {
  /** Current constraints */
  value: string[]
  /** Change handler */
  onChange: (constraints: string[]) => void
  /** Maximum number of constraints */
  maxConstraints?: number
  /** Input placeholder for simple mode */
  placeholder?: string
  /** Whether input is disabled */
  disabled?: boolean
  /** Read-only mode - shows only data without any edit controls */
  readOnly?: boolean
  /** Default input mode */
  defaultMode?: ConstraintInputMode
  /** Show mode toggle */
  showModeToggle?: boolean
  /** Show templates in simple mode */
  showTemplates?: boolean
  /** Additional CSS classes */
  className?: string
}

/**
 * Dynamic list input for constraints with add/remove capability.
 * Supports two modes:
 * - Simple: Free-text input with optional quick-add templates
 * - Builder: Structured input with category/subtype selection
 * 
 * Constraints follow the format: category:subtype:value
 * 
 * @example
 * ```tsx
 * <ConstraintsInput
 *   value={constraints}
 *   onChange={setConstraints}
 *   maxConstraints={10}
 *   defaultMode="builder"
 * />
 * ```
 */
export function ConstraintsInput({
  value,
  onChange,
  maxConstraints = 10,
  placeholder = 'Add a constraint (e.g., scope:path:Only modify src/**)',
  disabled = false,
  readOnly = false,
  defaultMode = 'simple',
  showModeToggle = true,
  showTemplates = true,
  className,
}: ConstraintsInputProps) {
  const [mode, setMode] = React.useState<ConstraintInputMode>(defaultMode)
  const [simpleInput, setSimpleInput] = React.useState('')

  const handleAdd = (constraint: string) => {
    if (value.length < maxConstraints && constraint.trim()) {
      onChange([...value, constraint.trim()])
    }
  }

  const handleSimpleAdd = () => {
    if (simpleInput.trim()) {
      handleAdd(simpleInput.trim())
      setSimpleInput('')
    }
  }

  const handleRemove = (index: number) => {
    const newConstraints = value.filter((_, i) => i !== index)
    onChange(newConstraints)
  }

  const handleChange = (index: number, newValue: string) => {
    const newConstraints = [...value]
    newConstraints[index] = newValue
    onChange(newConstraints)
  }

  const canAddMore = value.length < maxConstraints

  // Read-only mode: simple list without any edit controls
  if (readOnly) {
    if (value.length === 0) {
      return (
        <p className="text-sm text-muted-foreground">No constraints</p>
      )
    }
    return (
      <ul className={cn('space-y-1.5', className)}>
        {value.map((constraint, index) => {
          const parsed = parseConstraint(constraint)
          return (
            <li key={index} className="flex items-center gap-2 text-sm">
              {parsed.isStructured && (
                <ConstraintBadge constraint={constraint} />
              )}
              <span className={cn(
                parsed.isStructured ? 'text-muted-foreground' : ''
              )}>
                {parsed.isStructured ? parsed.value : constraint}
              </span>
            </li>
          )
        })}
      </ul>
    )
  }

  return (
    <div className={cn('space-y-3', className)}>
      {/* Existing constraints list */}
      {value.length > 0 && (
        <div className="space-y-2">
          {value.map((constraint, index) => {
            const parsed = parseConstraint(constraint)
            return (
              <div key={index} className="flex items-start gap-2 group">
                <div className="flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    {parsed.isStructured && (
                      <ConstraintBadge constraint={constraint} />
                    )}
                    <Input
                      value={constraint}
                      onChange={(e) => handleChange(index, e.target.value)}
                      placeholder={placeholder}
                      disabled={disabled}
                      className={cn(
                        'flex-1 text-sm',
                        parsed.isStructured && 'font-mono text-xs'
                      )}
                    />
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemove(index)}
                  disabled={disabled}
                  aria-label="Remove constraint"
                  className="h-9 w-9 opacity-50 group-hover:opacity-100"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )
          })}
        </div>
      )}

      {/* Add new constraint section */}
      {canAddMore && (
        <div className="space-y-3">
          {/* Mode toggle */}
          {showModeToggle && (
            <div className="flex items-center justify-end gap-1">
              <Button
                type="button"
                variant={mode === 'simple' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setMode('simple')}
                disabled={disabled}
                className="h-7 text-xs px-2"
              >
                <Wand2 className="h-3 w-3 mr-1" />
                Simple
              </Button>
              <Button
                type="button"
                variant={mode === 'builder' ? 'secondary' : 'ghost'}
                size="sm"
                onClick={() => setMode('builder')}
                disabled={disabled}
                className="h-7 text-xs px-2"
              >
                <Settings2 className="h-3 w-3 mr-1" />
                Builder
              </Button>
            </div>
          )}

          {/* Simple mode: free text input */}
          {mode === 'simple' && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Input
                  value={simpleInput}
                  onChange={(e) => setSimpleInput(e.target.value)}
                  placeholder={placeholder}
                  disabled={disabled}
                  className="flex-1"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && simpleInput.trim()) {
                      e.preventDefault()
                      handleSimpleAdd()
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleSimpleAdd}
                  disabled={disabled || !simpleInput.trim()}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>

              {showTemplates && (
                <ConstraintTemplates
                  onAdd={handleAdd}
                  existingConstraints={value}
                  disabled={disabled}
                />
              )}
            </div>
          )}

          {/* Builder mode: structured input */}
          {mode === 'builder' && (
            <ConstraintBuilder onAdd={handleAdd} disabled={disabled} />
          )}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {value.length} / {maxConstraints} constraints
        </span>
        {!canAddMore && (
          <span className="text-amber-600 dark:text-amber-400">
            Maximum reached
          </span>
        )}
      </div>
    </div>
  )
}
